import { useMemo } from 'react';

import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';

import { CfsApi } from '@/api';
import { Chip } from '@/components';
import { ASSIGNMENT_STATUS } from '@/constants';
import { AssignmentStatus, ICFS, IncomingCallCFSFormValues } from '@/models';
import { getAddressLocation } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { openNewWindow } from '@/utils';

import { RecentCFSCardRoot } from '../styles';

export const RecentCFSCard = ({
  _id,
  number,
  shortDescription,
  assignmentStatus,
  isClosed,
  isReadyToDispatch,
  fireControlled,
  addressInfo,
  onClose,
  incomingCallId,
  disableClose,
  getValues,
}: ICFS & {
  onClose: () => void;
  incomingCallId: string;
  disableClose?: boolean;
  getValues: () => Partial<Pick<IncomingCallCFSFormValues, 'notes'>>;
}) => {
  const { updateToast } = useToastStore();
  const address = addressInfo?.address;

  const statusDisplay = useMemo(() => {
    if (isClosed) {
      return { label: 'Closed', color: colors.grey[10], textColor: 'black' };
    }
    if (fireControlled) {
      return {
        label: 'Fire Controlled',
        color: '#97CC6D',
        textColor: colors.white,
      };
    }
    if (isReadyToDispatch && assignmentStatus === AssignmentStatus.NEW) {
      return {
        label: 'Ready for Dispatch',
        color: colors.warning.main,
        textColor: colors.white,
      };
    }
    return {
      label: ASSIGNMENT_STATUS[assignmentStatus]?.label ?? 'Unknown',
      color: ASSIGNMENT_STATUS[assignmentStatus]?.color ?? colors.grey[10],
      textColor: colors.white,
    };
  }, [isClosed, fireControlled, isReadyToDispatch, assignmentStatus]);
  const handleOpenClick = async (id?: string) => {
    if (id) await openNewWindow(`/cfs/${id}`, id);
    if (!disableClose) {
      onClose();
    }
  };

  const handleDuplicateClick = async (id?: string) => {
    if (id) {
      const values = getValues();
      CfsApi.update(id, {
        incomingCallId,
        disposition: 'Duplicate',
        incomingCallNotes: values.notes ? values.notes : '',
      })
        .then(() => {
          updateToast({
            type: ConfirmTypes.SUCCESS,
            message: 'CFS duplicated successfully',
          });
        })
        .catch(() => {
          updateToast({
            type: ConfirmTypes.ERROR,
            message: 'Failed to duplicate CFS',
          });
        });
    }
    onClose();
  };

  return (
    <RecentCFSCardRoot key={number}>
      <Stack className="call-chips">
        <Chip
          label={number}
          sx={{
            color: colors.white,
            backgroundColor: colors.primary.main,
          }}
        />

        <Chip
          label={statusDisplay.label}
          sx={{
            color: statusDisplay.textColor,
            backgroundColor: statusDisplay.color,
          }}
        />
      </Stack>
      <Stack className="call-info-wrapper">
        <Box>
          <Typography>{getAddressLocation(address)}</Typography>

          <Typography>
            <Tooltip title={shortDescription} className="cfs-description">
              <Box>{shortDescription}</Box>
            </Tooltip>
          </Typography>
        </Box>
        <Stack className="call-action">
          <Button
            variant="contained"
            color="inherit"
            onClick={() => handleOpenClick(_id)}
          >
            Open
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDuplicateClick(_id)}
          >
            Duplicate
          </Button>
        </Stack>
      </Stack>
    </RecentCFSCardRoot>
  );
};
